import React, {useEffect, useState} from "react";
import {
  Button,
  Card,
  CardHeader,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  MenuItem,
  Paper,
  TextField
} from "@mui/material";
import {useTranslation} from "react-i18next";
import {
  CHART_AXES_EXTREMITIES_AUTOMATIC,
  CHART_AXES_EXTREMITIES_DEFAULT,
  CHART_AXES_EXTREMITIES_FROM_ZERO,
  CHART_DATA_LABEL_TYPE_NONE,
  CHART_DATA_LABEL_TYPE_VALUE,
  CHART_LEGEND_POSITION_BOTTOM,
  CHART_LEGEND_POSITION_lEFT,
  CHART_LEGEND_POSITION_RIGHT,
  CHART_LEGEND_POSITION_TOP
} from "../chart/constants";
import CustomDialog from "../custom-dialog";
import CustomDialogTitle from "../custom-dialog-title";
import I18nTextField from "../i18n-text-field";

const fieldStyle = {
  margin: "8px 0"
};

const paperStyle = {
  padding: "16px"
};

const cardHeaderStyle = {
  padding: "0 0 8px 0"
};

const SettingsDialog = ({
  isSettingsDialogVisible,
  setIsSettingsDialogVisible,
  chartSettings,
  setChartSettings,
  onChartSettingsChange
}) => {
  const {t} = useTranslation();

  const [settings, setSettings] = useState(null);

  useEffect(() => {
    if (chartSettings) {
      setSettings({...chartSettings});
    }
  }, [chartSettings]);

  const handleSettingsDialogClose = () => setIsSettingsDialogVisible(false);
  const handleSettingsDialogSubmit = () => {
    setIsSettingsDialogVisible(false);
    setChartSettings(settings);
    if (onChartSettingsChange) onChartSettingsChange(settings);
  };

  return (
    <CustomDialog id="viewer-chart__settings_dialog" open={isSettingsDialogVisible} onClose={handleSettingsDialogClose}>
      <CustomDialogTitle onClose={handleSettingsDialogClose}>
        {t("components.chartSettings.header.general")}
      </CustomDialogTitle>
      <DialogContent>
        <FormControl fullWidth sx={fieldStyle}>
          <TextField
            id="chart-settings-form__legend-position"
            select
            fullWidth
            label={t("components.chartSettings.general.legendPosition.label")}
            value={settings?.legendPosition ?? CHART_LEGEND_POSITION_TOP}
            variant="outlined"
            onChange={ev => setSettings({...settings, legendPosition: ev.target.value})}
          >
            <MenuItem id="chart-settings-form__legend-position-top" value={CHART_LEGEND_POSITION_TOP}>
              {t("components.chartSettings.general.legendPosition.values.top")}
            </MenuItem>
            <MenuItem id="chart-settings-form__legend-position-right" value={CHART_LEGEND_POSITION_RIGHT}>
              {t("components.chartSettings.general.legendPosition.values.right")}
            </MenuItem>
            <MenuItem id="chart-settings-form__legend-position-bottom" value={CHART_LEGEND_POSITION_BOTTOM}>
              {t("components.chartSettings.general.legendPosition.values.bottom")}
            </MenuItem>
            <MenuItem id="chart-settings-form__legend-position-left" value={CHART_LEGEND_POSITION_lEFT}>
              {t("components.chartSettings.general.legendPosition.values.left")}
            </MenuItem>
          </TextField>
        </FormControl>
        <FormControl fullWidth sx={fieldStyle}>
          <FormControlLabel
            label={t("components.chartSettings.general.dataLabelType.label")}
            control={
              <Checkbox
                id="chart-settings-form__data-label-type"
                checked={settings?.dataLabelType === CHART_DATA_LABEL_TYPE_VALUE}
                onChange={(e, value) => {
                  setSettings({
                    ...settings,
                    dataLabelType: value ? CHART_DATA_LABEL_TYPE_VALUE : CHART_DATA_LABEL_TYPE_NONE
                  });
                }}
              />
            }
          />
        </FormControl>
        <Card variant="outlined" sx={paperStyle}>
          <CardHeader subheader={t("components.chartSettings.header.chartCartesian")} sx={cardHeaderStyle} />
          <FormControl fullWidth sx={fieldStyle}>
            <FormControlLabel
              label={t("components.chartSettings.general.isStacked.label")}
              control={
                <Checkbox
                  id="chart-settings-form__is-stacked"
                  checked={settings?.stacked ?? false}
                  onChange={(e, value) => setSettings({...settings, stacked: value})}
                />
              }
            />
          </FormControl>
          <FormControl fullWidth sx={fieldStyle}>
            <FormControlLabel
              label={t("components.chartSettings.general.showAxesLabel.label")}
              control={
                <Checkbox
                  id="chart-settings-form__show-axes-label"
                  checked={settings?.showAxesLabel ?? false}
                  onChange={(e, value) => setSettings({...settings, showAxesLabel: value})}
                />
              }
            />
          </FormControl>
          {settings?.showAxesLabel && (
            <Paper variant="outlined" sx={paperStyle}>
              <>
                <FormControl id="chart-settings-form__value-axis-label" fullWidth sx={fieldStyle}>
                  <I18nTextField
                    label={t("components.chartSettings.general.valueAxisLabel.label")}
                    variant="outlined"
                    value={settings?.valueAxisLabel ?? ""}
                    onChange={value => setSettings({...settings, valueAxisLabel: value})}
                    disabled={!settings?.showAxesLabel}
                  />
                </FormControl>
                <FormControl fullWidth sx={fieldStyle}>
                  <FormControlLabel
                    label={t("components.chartSettings.general.customizeCategoryAxis.label")}
                    control={
                      <Checkbox
                        id="chart-settings-form__customize-category-axis"
                        checked={settings?.customizeCategoryAxis ?? false}
                        onChange={(e, value) => setSettings({...settings, customizeCategoryAxis: value})}
                        disabled={!settings?.showAxesLabel}
                      />
                    }
                  />
                </FormControl>
                {settings?.customizeCategoryAxis && (
                  <FormControl id="chart-settings-form__category-axis-label" fullWidth sx={fieldStyle}>
                    <I18nTextField
                      label={t("components.chartSettings.general.categoryAxisLabel.label")}
                      variant="outlined"
                      value={settings?.categoryAxisLabel ?? ""}
                      onChange={value => setSettings({...settings, categoryAxisLabel: value})}
                      disabled={!settings?.customizeCategoryAxis || !settings?.showAxesLabel}
                    />
                  </FormControl>
                )}
              </>
            </Paper>
          )}
          <FormControl fullWidth sx={fieldStyle}>
            <TextField
              id="chart-settings-form__extremities-cartesian-axes-graphs"
              select
              fullWidth
              label={t("components.chartSettings.general.extremitiesCartesianAxesGraphs.label")}
              value={settings?.extremitiesCartesianAxesGraphs || CHART_AXES_EXTREMITIES_DEFAULT}
              variant="outlined"
              onChange={ev => setSettings({...settings, extremitiesCartesianAxesGraphs: ev.target.value})}
            >
              <MenuItem
                id="chart-settings-form__extremities-cartesian-axes-graphs-default"
                value={CHART_AXES_EXTREMITIES_DEFAULT}
              >
                {t("components.chartSettings.general.extremitiesCartesianAxesGraphs.values.default")}
              </MenuItem>
              <MenuItem
                id="chart-settings-form__extremities-cartesian-axes-graphs-automatic"
                value={CHART_AXES_EXTREMITIES_AUTOMATIC}
              >
                {t("components.chartSettings.general.extremitiesCartesianAxesGraphs.values.automatic")}
              </MenuItem>
              <MenuItem
                id="chart-settings-form__extremities-cartesian-axes-graphs-from-zero"
                value={CHART_AXES_EXTREMITIES_FROM_ZERO}
              >
                {t("components.chartSettings.general.extremitiesCartesianAxesGraphs.values.fromZero")}
              </MenuItem>
            </TextField>
          </FormControl>
        </Card>
      </DialogContent>
      <DialogActions>
        <Button id="viewer-chart__settings_dialog__cancel-btn" onClick={handleSettingsDialogClose}>
          {t("commons.confirm.cancel")}
        </Button>
        <Button id="viewer-chart__settings_dialog__apply-btn" color="primary" onClick={handleSettingsDialogSubmit}>
          {t("commons.confirm.apply")}
        </Button>
      </DialogActions>
    </CustomDialog>
  );
};

export default SettingsDialog;
